/* The code is defining a custom hook called `useApi` that provides functionality for making API
requests using Axios. */
// Import necessary dependencies
import axios from "axios";
import notify from "../components/common/notify";
import { accessToken } from "../utils/localData";
import { useState } from "react";

// Create Axios instance with token for authenticated requests
const AXIOS_WITH_TOKEN = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

// Add interceptor to include authorization header with access token for authenticated requests
AXIOS_WITH_TOKEN.interceptors.request.use((config) => {
  if (accessToken()) {
    config.headers.Authorization = `Bearer ${accessToken()}`;
  }
  return config;
});

// Create Axios instance without token for unauthenticated requests
const AXIOS_WITHOUT_TOKEN = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

// Function to handle API request errors and show notifications
export const handleError = (error, onError, notifyError) => {
  const { data, message } = error?.data || {};

  // Call custom error handler if provided
  if (onError) {
    onError(error);
  }

  // Show individual error notifications for each item in the 'data' array
  if (data?.length > 0 && notifyError) {
    data.forEach((item) => notify(item, "error"));
    return;
  }

  // Show network connection error notification
  if (error.status === "FETCH_ERROR") {
    notify("Please check your network connection.", "error");
  }

  // Show user rejection error notification
  if (error?.code === "ACTION_REJECTED") {
    notify("User rejected action.", "error");
  }

  // Show general error notification from the 'message' or 'data.message' property
  if ((message || data?.message) && notifyError) {
    notify(message || data?.message, "error");
  }

  // Show short message error notification
  if (error?.shortMessage) {
    notify(error?.shortMessage, "error");
  }

  // Return the original error for further handling
  return error;
};

// Custom React hook for handling API requests
const useApi = () => {
  // State variable to track loading state
  const [loading, setLoading] = useState(false);

  // Function to handle API requests
  const requestHandler = async ({
    url,
    method = "get",
    data = null,
    onSuccess = null,
    onError = null,
    API,
    notifyError = false,
  }) => {
    // Set loading state to true
    setLoading(true);

    try {
      // Make the API request using the specified Axios instance
      const response = await API({
        method,
        url,
        data,
      });

      // Call custom success handler if provided
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Set loading state to false and return the response data
      setLoading(false);
      return response.data;
    } catch (error) {
      // Set loading state to false and handle the error
      setLoading(false);
      handleError(error, onError, notifyError);
    }
  };

  // Function to make authenticated API requests
  const PRIVATE_REQUEST = (props) => {
    return requestHandler({ ...props, API: AXIOS_WITH_TOKEN });
  };

  // Function to make unauthenticated (public) API requests
  const PUBLIC_REQUEST = (props) => {
    return requestHandler({ ...props, API: AXIOS_WITHOUT_TOKEN });
  };

  // Return an object containing the loading state and API request functions
  return { loading, PRIVATE_REQUEST, PUBLIC_REQUEST };
};

// Export the custom hook
export default useApi;
